import * as React from "react";
import {
  Button,
  TextField,
  Autocomplete
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

import "../../../styles/pages/rfHistoryModals.css";

/**
 * Update Condo Received Detail Modal Component
 * Modal for updating received information for an RF
 * Includes validation and consistent styling with other modals
 */
export default function UpdateReceivedModal({ open, onClose, rfData }) {
  const { showToast } = useToast();

  const [formData, setFormData] = React.useState({
    receivedBy: "",
    receivedOn: ""
  });

  const [errors, setErrors] = React.useState({
    receivedBy: "",
    receivedOn: ""
  });

  // Hardcoded options for Received By - can be expanded with more names
  const receivedByOptions = [
    "Hew Chin Chew",
    "Arlene Kam",
    "John Doe",
    "Jane Smith",
    "Michael Brown",
    "Sarah Johnson",
    "David Lee",
    "Emily Chen",
    "Robert Wilson",
    "Lisa Anderson"
  ];

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (open && rfData) {
      setFormData({
        receivedBy: rfData.received?.by || "",
        receivedOn: rfData.received?.on || ""
      });
      setErrors({
        receivedBy: "",
        receivedOn: ""
      });
    }
  }, [open, rfData]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.receivedBy || formData.receivedBy === "") {
      newErrors.receivedBy = "Received By is required";
      isValid = false;
    }

    if (!formData.receivedOn || formData.receivedOn.trim() === "") {
      newErrors.receivedOn = "Received On is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleUpdate = () => {
    if (!validateForm()) {
      showToast({
        message: "Please fill in all required fields",
        severity: "error"
      });
      return;
    }

    // Backend developer will add API call here
    showToast({
      message: "Received details updated successfully",
      severity: "success"
    });
    onClose();
  };

  const handleClose = () => {
    setFormData({
      receivedBy: "",
      receivedOn: ""
    });
    setErrors({
      receivedBy: "",
      receivedOn: ""
    });
    onClose();
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={handleClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handleUpdate}>
        Update
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Update Condo Received Detail"
      actions={actions}
      maxWidth="sm"
      fullWidth
    >
      <div className="update-received-modal-content">
        {/* Received By Field - Autocomplete with search */}
        <Autocomplete
          fullWidth
          id="received-by"
          options={receivedByOptions}
          value={formData.receivedBy || null}
          onChange={(event, newValue) => {
            setFormData((prev) => ({
              ...prev,
              receivedBy: newValue || ""
            }));
            if (errors.receivedBy) {
              setErrors((prev) => ({
                ...prev,
                receivedBy: ""
              }));
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                <>
                  Received By <span className="required-asterisk">*</span>
                </>
              }
              error={!!errors.receivedBy}
              helperText={errors.receivedBy}
              placeholder="Type to search..."
            />
          )}
        />

        {/* Received On Field */}
        <TextField
          fullWidth
          size="small"
          id="received-on"
          type="date"
          label={
            <>
              Received On <span className="required-asterisk">*</span>
            </>
          }
          value={formData.receivedOn}
          onChange={handleChange("receivedOn")}
          error={!!errors.receivedOn}
          helperText={errors.receivedOn}
          InputLabelProps={{ shrink: true }}
        />
      </div>
    </Modal>
  );
}
